"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const painPoints = [
    {
        num: '01',
        tag: 'Reach Problem',
        problem: 'Posting But Not Growing',
        desc: "You're consistent with content but follower counts and reach are flat. Your posts disappear into the feed with zero traction.",
    },
    {
        num: '02',
        tag: 'Conversion Problem',
        problem: "Likes Don't Pay Bills",
        desc: 'High engagement on some posts, zero enquiries. Your social media feels disconnected from actual business outcomes and leads.',
    },
    {
        num: '03',
        tag: 'Brand Problem',
        problem: 'No Brand Recall',
        desc: "People scroll past your content without remembering who you are. Your brand has no distinct voice, look, or identity in the feed.",
    },
];

export default function SocialChallenges() {
    const [hovered, setHovered] = useState<number | null>(null);

    return (
        <section className="relative overflow-hidden py-28 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>

            <div className="max-w-[1400px] mx-auto flex flex-col gap-16 relative z-10">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            Sound Familiar?
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(34px, 4.5vw, 62px)', color: '#fbfdfd', lineHeight: 1.05, letterSpacing: '-0.02em' }}
                        >
                            Is Your Social Media<br />Just Making Noise?
                        </motion.h2>
                    </div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
                        className="max-w-sm text-base leading-relaxed"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.40)', letterSpacing: '0.02em' }}
                    >
                        Most businesses face the same three problems on social. They&apos;re visible — just not memorable, trusted, or converting.
                    </motion.p>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {painPoints.map((pt, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 36 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(null)}
                            className="relative overflow-hidden rounded-2xl flex flex-col gap-6 p-8 cursor-default"
                            style={{
                                border: hovered === i ? '1px solid rgba(206,255,26,0.35)' : '1px solid rgba(206,255,26,0.10)',
                                background: hovered === i ? 'rgba(206,255,26,0.04)' : 'rgba(255,255,255,0.02)',
                                transition: 'all 0.3s ease',
                                minHeight: 320,
                            }}
                        >
                            {/* Giant faded number background */}
                            <span
                                className="absolute pointer-events-none select-none"
                                style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 900,
                                    fontSize: 'clamp(100px, 14vw, 160px)',
                                    lineHeight: 1,
                                    color: hovered === i ? 'rgba(206,255,26,0.06)' : 'rgba(251,253,253,0.035)',
                                    right: -8,
                                    top: -12,
                                    letterSpacing: '-0.05em',
                                    transition: 'color 0.3s ease',
                                    userSelect: 'none',
                                }}
                            >
                                {pt.num}
                            </span>

                            {/* Tag */}
                            <div
                                className="self-start"
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    color: hovered === i ? '#ceff1a' : 'rgba(206,255,26,0.40)',
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    border: `1px solid ${hovered === i ? 'rgba(206,255,26,0.35)' : 'rgba(206,255,26,0.15)'}`,
                                    borderRadius: 99,
                                    padding: '4px 12px',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                {pt.tag}
                            </div>

                            {/* Title */}
                            <h3
                                style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 700,
                                    fontSize: 'clamp(20px, 1.8vw, 26px)',
                                    color: '#fbfdfd',
                                    letterSpacing: '0.01em',
                                    lineHeight: 1.2,
                                }}
                            >
                                {pt.problem}
                            </h3>

                            {/* Description */}
                            <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 14, lineHeight: 1.8, color: 'rgba(251,253,253,0.50)', letterSpacing: '0.02em' }}>
                                {pt.desc}
                            </p>

                            {/* Bottom bar */}
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #ceff1a 0%, transparent 80%)', transform: hovered === i ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left', transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)' }} />
                        </motion.div>
                    ))}
                </div>

                {/* Conclusion line */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                    className="flex items-center gap-4"
                >
                    <div style={{ flex: 1, height: 1, background: 'rgba(206,255,26,0.10)' }} />
                    <p className="text-center md:whitespace-nowrap" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 600, fontSize: 14, color: 'rgba(206,255,26,0.50)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        We fix all three — with strategy, not just content
                    </p>
                    <div style={{ flex: 1, height: 1, background: 'rgba(206,255,26,0.10)' }} />
                </motion.div>

            </div>
        </section>
    );
}
